import { supabase } from '../supabase';
import * as Sentry from '@sentry/react';
import { trainingDay } from '../utils/trainingDay';

// Swaps belong to the client's training day (utils/trainingDay: their own day, from 3 am)
const today = () => trainingDay();

// Returns the swapped exercise object, null if no swap exists today, or
// undefined if the query itself failed (auth/network) — callers must treat
// undefined as "keep current state" rather than "no swap".
export async function loadSessionSwap(userId, programExerciseId) {
  const { data, error } = await supabase
    .from('session_overrides')
    .select('swapped_exercise:swapped_exercise_id(id, name, gif_url, exercise_type, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier, duration_unit, is_unilateral)')
    .eq('user_id', userId)
    .eq('program_exercise_id', programExerciseId)
    .eq('session_date', today())
    .maybeSingle();
  if (error) { Sentry.captureException(new Error(error.message), { extra: { fn: 'loadSessionSwap', supabaseError: error, userId, programExerciseId } }); return undefined; }
  return data?.swapped_exercise ?? null;
}

// The quick reasons a client can give for a swap. `code` is what the database stores (migration 063
// checks it); `label` is what the client taps and what the coach reads.
export const SWAP_REASONS = [
  { code: 'equipment_taken', label: 'Equipment taken' },
  { code: 'no_equipment', label: 'No equipment' },
  { code: 'discomfort', label: 'Pain or discomfort' },
  { code: 'preference', label: 'Prefer this' },
];
export const swapReasonLabel = (code) => SWAP_REASONS.find((r) => r.code === code)?.label ?? null;

// PostgREST's (PGRST204) and Postgres' (42703) "there is no such column": migration 063 is not applied.
const isMissingColumn = (error) => error?.code === 'PGRST204' || error?.code === '42703';

// `originalExerciseId` is the exercise being swapped AWAY from. It is recorded on the row because the
// slot (`program_exercise_id`, really a block_items id) has no foreign key and its exercise can be
// changed later, so without it "what was this swapped from?" cannot be answered afterwards.
export async function saveSessionSwap(userId, programExerciseId, swappedExerciseId, originalExerciseId = null) {
  const swap = { user_id: userId, program_exercise_id: programExerciseId, swapped_exercise_id: swappedExerciseId, session_date: today() };
  const options = { onConflict: 'user_id,program_exercise_id,session_date' };

  // `reason: null` clears a reason left by an earlier swap of this slot today. An upsert only touches
  // the columns it is given, so leaving it out would carry the old reason onto the new swap.
  let { error } = await supabase
    .from('session_overrides')
    .upsert({ ...swap, original_exercise_id: originalExerciseId, reason: null }, options);

  // Swapping must never break because the database is one migration behind the app. Without 063 the
  // two new columns do not exist, so save the swap the way it was saved before them.
  if (isMissingColumn(error)) {
    ({ error } = await supabase.from('session_overrides').upsert(swap, options));
  }
  if (error) throw error;
}

// Records why the client swapped. Optional, and after the fact: the swap itself is already saved.
export async function saveSwapReason(userId, programExerciseId, reason) {
  const { error } = await supabase
    .from('session_overrides')
    .update({ reason })
    .eq('user_id', userId)
    .eq('program_exercise_id', programExerciseId)
    .eq('session_date', today());
  if (error) throw error;
}

// How far back the coach's "Client swaps" panel looks. Long enough to span two training blocks, so a
// swap repeated week after week shows as a pattern; short enough that last season's does not linger.
export const SWAP_HISTORY_DAYS = 60;

// The first date in that window, as session_overrides stores dates: the training day, YYYY-MM-DD.
export const swapHistorySince = (now = new Date()) =>
  trainingDay(new Date(now.getTime() - SWAP_HISTORY_DAYS * 24 * 60 * 60 * 1000));

// A client's swaps on or after `sinceDate` (YYYY-MM-DD), newest first, for their coach to read
// (migration 063 grants that). Returns null if the query failed, so the screen can say "could not
// load" instead of "no swaps".
export async function loadClientSwaps(clientUserId, sinceDate) {
  const { data, error } = await supabase
    .from('session_overrides')
    .select('id, session_date, reason, original:original_exercise_id(id, name), swapped:swapped_exercise_id(id, name)')
    .eq('user_id', clientUserId)
    .gte('session_date', sinceDate)
    .order('session_date', { ascending: false });
  if (error) {
    Sentry.captureException(new Error(error.message), { extra: { fn: 'loadClientSwaps', supabaseError: error, clientUserId } });
    return null;
  }
  return data ?? [];
}

// Groups swaps by the exercise the client swapped AWAY from, which is the thing a coach acts on
// ("she never does the hip thrust"), not by day. Each group carries how often, when last, what it was
// swapped for and why. Pain comes first, then the most-repeated, then the most recent.
// Swaps with no recorded original (made before migration 063 and not recoverable) share one group.
export function summariseSwaps(rows) {
  const groups = new Map();
  for (const row of rows || []) {
    const key = row.original?.id != null ? `ex:${row.original.id}` : 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        originalId: row.original?.id ?? null,
        originalName: row.original?.name ?? null,
        count: 0,
        lastDate: row.session_date,
        replacements: new Map(),
        reasons: new Map(),
      });
    }
    const group = groups.get(key);
    group.count += 1;
    if (row.session_date > group.lastDate) group.lastDate = row.session_date;
    const replacement = row.swapped?.name ?? 'Unknown exercise';
    group.replacements.set(replacement, (group.replacements.get(replacement) || 0) + 1);
    if (row.reason) group.reasons.set(row.reason, (group.reasons.get(row.reason) || 0) + 1);
  }

  const byCountThenName = (a, b) => b.count - a.count || a.name.localeCompare(b.name);
  return [...groups.values()]
    .map((group) => ({
      ...group,
      replacements: [...group.replacements].map(([name, count]) => ({ name, count })).sort(byCountThenName),
      reasons: [...group.reasons]
        .map(([code, count]) => ({ code, name: swapReasonLabel(code) ?? code, count }))
        .sort(byCountThenName),
      isRepeat: group.count >= 2,
      hasDiscomfort: group.reasons.has('discomfort'),
    }))
    .sort((a, b) =>
      Number(b.hasDiscomfort) - Number(a.hasDiscomfort)
      || b.count - a.count
      || b.lastDate.localeCompare(a.lastDate));
}

// A long swap list is hard to act on, so the coach's panel sorts the groups by what to do about them:
//   actOn      pain was given as a reason, or the same exercise was swapped SWAP_ACT_ON_COUNT times or
//              more: a pattern, not a one-off. Each carries a plain line saying what is worth doing.
//   equipment  swapped fewer times, and every reason given was the gym not having it or it being taken.
//   occasional everything else (once or twice, a preference or no reason), plus swaps whose original
//              was never recorded, which there is nothing to act on.
// Takes summariseSwaps' groups (already ordered) and keeps their order within each part.
export const SWAP_ACT_ON_COUNT = 3;
const EQUIPMENT_REASONS = ['no_equipment', 'equipment_taken'];

const swapAdvice = (group, gymReasons) => {
  if (group.hasDiscomfort) return 'Pain or discomfort was given as a reason. Worth asking how it feels, or taking it out of the program.';
  if (gymReasons) return `Swapped ${group.count} times because the gym did not have it or it was taken. Worth replacing it in the program.`;
  return `Swapped ${group.count} times, most often for ${group.replacements[0]?.name ?? 'something else'}. Worth replacing it in the program.`;
};

export function structureSwaps(groups) {
  const actOn = [];
  const equipment = [];
  const occasional = [];
  for (const group of groups || []) {
    const gymReasons = group.reasons.length > 0 && group.reasons.every((r) => EQUIPMENT_REASONS.includes(r.code));
    const known = group.key !== 'unknown';
    if (known && (group.hasDiscomfort || group.count >= SWAP_ACT_ON_COUNT)) {
      actOn.push({ ...group, advice: swapAdvice(group, gymReasons) });
    } else if (known && gymReasons) {
      equipment.push(group);
    } else {
      occasional.push(group);
    }
  }
  return {
    actOn,
    equipment,
    occasional,
    swapCount: (groups || []).reduce((sum, g) => sum + g.count, 0),
    exerciseCount: (groups || []).filter((g) => g.key !== 'unknown').length,
  };
}

export async function clearSessionSwap(userId, programExerciseId) {
  const { error } = await supabase
    .from('session_overrides')
    .delete()
    .eq('user_id', userId)
    .eq('program_exercise_id', programExerciseId)
    .eq('session_date', today());
  if (error) throw error;
}

export async function fetchAlternatives(movementCategory, excludeExerciseId) {
  const { data, error } = await supabase
    .from('exercises')
    .select('id, name, gif_url, exercise_type, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier, duration_unit, is_unilateral')
    .eq('movement_category', movementCategory)
    .neq('id', excludeExerciseId)
    .order('name')
    .limit(20);
  if (error) Sentry.captureException(new Error(error.message), { extra: { fn: 'fetchAlternatives', supabaseError: error, movementCategory } });
  return data ?? [];
}

export async function searchExercises(keyword, excludeExerciseId) {
  const { data, error } = await supabase
    .from('exercises')
    .select('id, name, gif_url, exercise_type, movement_category, is_bodyweight, is_assisted, bodyweight_multiplier, duration_unit, is_unilateral')
    .ilike('name', `%${keyword}%`)
    .neq('id', excludeExerciseId)
    .order('name')
    .limit(30);
  if (error) Sentry.captureException(new Error(error.message), { extra: { fn: 'searchExercises', supabaseError: error, keyword } });
  return data ?? [];
}
